import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Typography, Select, Tag, Button, Space, Spin, Alert, Drawer, Input, theme } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconMessage, IconArrowUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  getAdminConversations,
  getAdminConversationMessages,
  sendAdminConversationMessage,
  closeAdminConversation,
  reopenAdminConversation,
} from '../../../services/AdminService';
import { onReceiveAdminMessage } from '../../../services/RealtimeService';
import { IAdminConversationMessage, IAdminConversationSummary } from '../../../interfaces/heavyDutyMachinery/IAdminConversation';
import EmptyState from '../../../components/EmptyState';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { CORAL } from '../../../theme/creamyTokens';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, enumColumnFilters } from '../../../helpers/adminTableHelpers';
import { useAppBreakpoint } from '../../../hooks/useAppBreakpoint';

const { Title, Text } = Typography;
const { TextArea } = Input;

const STATUS_COLORS: Record<string, string> = { Open: 'processing', Closed: 'default' };
const STATUS_LABEL_KEYS: Record<string, string> = {
  Open: TRANSLATIONS.ADMIN_MESSAGES_STATUS_OPEN,
  Closed: TRANSLATIONS.ADMIN_MESSAGES_STATUS_CLOSED,
};
const ROLE_LABEL_KEYS: Record<string, string> = {
  Host: TRANSLATIONS.ADMIN_MESSAGES_ROLE_HOST,
  Guest: TRANSLATIONS.ADMIN_MESSAGES_ROLE_GUEST,
};
const STATUS_OPTIONS = ['Open', 'Closed'];

/**
 * Admin panel's "Mensajes" section -- the shared inbox for every
 * AdminConversation (see the backend entity's own doc comment: one per user,
 * ever, opened by an Admin/Moderator). No "start a new conversation" composer
 * here on purpose -- that always happens from wherever the admin already has
 * a specific user in context (a host's detail drawer, a report, etc.); this
 * section is where the resulting threads live and get replied to/closed. No
 * live push either (see MyAdminConversationController.SendMessage's own doc
 * comment) -- reload the list to see new replies, same as every other admin
 * queue in this panel.
 */
const MessagesSection: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const [conversations, setConversations] = useState<IAdminConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState<string | undefined>(undefined);

  const [detail, setDetail] = useState<IAdminConversationSummary | null>(null);
  const [messages, setMessages] = useState<IAdminConversationMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  // Read from inside the realtime subscription below (registered once, see
  // its own comment) -- lets that closure always know which conversation is
  // currently open without re-subscribing on every Drawer open/close.
  const detailIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    detailIdRef.current = detail?.id;
  }, [detail]);

  const loadConversations = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminConversations(status)
      .then(setConversations)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Live push for every admin-chat message -- both a host/guest's reply
  // (see MyAdminConversationController.SendMessage) and this admin's OWN
  // sent message echoed back (AdminConversationsController.SendMessage
  // broadcasts to the whole group too, so a second admin with this section
  // open also sees it land). Registered once ([] deps): reads the currently
  // open conversation via detailIdRef rather than depending on `detail`
  // directly, so opening/closing the Drawer never tears this down.
  useEffect(() => {
    return onReceiveAdminMessage((incoming) => {
      setConversations((prev) => {
        const index = prev.findIndex((c) => c.id === incoming.conversationId);
        // Not loaded in the current page/filter -- nothing to patch, the
        // next reload will bring it in already up to date.
        if (index === -1) return prev;
        const isOpenNow = detailIdRef.current === incoming.conversationId;
        const updated: IAdminConversationSummary = {
          ...prev[index],
          lastMessageText: incoming.text,
          lastMessageAt: incoming.sentAt,
          unreadCount: incoming.isFromAdmin || isOpenNow ? prev[index].unreadCount : prev[index].unreadCount + 1,
        };
        const rest = prev.filter((_, i) => i !== index);
        return [updated, ...rest];
      });

      if (detailIdRef.current !== incoming.conversationId) return;
      // Deduped by id -- handleSend already appends this admin's own
      // message optimistically the moment it's sent, so the broadcast
      // arriving a beat later for that same message must not double it up.
      setMessages((prev) => (prev.some((m) => m.id === incoming.id) ? prev : [...prev, incoming]));
      requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }));
    });
  }, []);

  const openDetail = (conversation: IAdminConversationSummary) => {
    setDetail(conversation);
    setMessages([]);
    setText('');
    setMessagesLoading(true);
    getAdminConversationMessages(conversation.id)
      .then((page) => {
        setMessages(page.items);
        requestAnimationFrame(() => bottomRef.current?.scrollIntoView());
      })
      .catch(() => message.error(translate(t, TRANSLATIONS.ADMIN_MESSAGES_LOAD_ERROR)))
      .finally(() => setMessagesLoading(false));
  };

  const handleSend = async () => {
    if (!detail || !text.trim()) return;
    setSending(true);
    try {
      const sent = await sendAdminConversationMessage(detail.id, text.trim());
      // Deduped by id -- the ReceiveAdminMessage group broadcast (see the
      // subscription above) includes this admin's own connection, so on a
      // fast WebSocket it can append this exact message before this HTTP
      // response even comes back. Without this check, whichever of the two
      // arrives second would double it up.
      setMessages((prev) => (prev.some((m) => m.id === sent.id) ? prev : [...prev, sent]));
      setText('');
      requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }));
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_MESSAGES_ACTION_ERROR));
    } finally {
      setSending(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!detail) return;
    setStatusChanging(true);
    try {
      const updated = detail.status === 'Open' ? await closeAdminConversation(detail.id) : await reopenAdminConversation(detail.id);
      setDetail(updated);
      loadConversations();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_MESSAGES_ACTION_ERROR));
    } finally {
      setStatusChanging(false);
    }
  };

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_MESSAGES)}
      </Title>

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_MESSAGES_STATUS_FILTER_ALL)}
          style={{ maxWidth: 200, width: '100%' }}
          value={status}
          onChange={setStatus}
          options={STATUS_OPTIONS.map((value) => ({ value, label: translate(t, STATUS_LABEL_KEYS[value]) }))}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_MESSAGES_LOAD_ERROR)} />
      ) : conversations.length === 0 ? (
        <EmptyState
          icon={<IconMessage size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_MESSAGES_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_MESSAGES_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminConversationSummary>
          rowKey="id"
          dataSource={conversations}
          pagination={{ pageSize: 20 }}
          toCard={(record) => ({
            key: record.id,
            title: record.userDisplayName,
            subtitle: ROLE_LABEL_KEYS[record.userRole] ? translate(t, ROLE_LABEL_KEYS[record.userRole]) : undefined,
            badge: (
              <Tag color={STATUS_COLORS[record.status]} style={{ marginInlineEnd: 0 }}>
                {translate(t, STATUS_LABEL_KEYS[record.status])}
              </Tag>
            ),
            fields: [
              { label: translate(t, TRANSLATIONS.ADMIN_MESSAGES_COL_SUBJECT), value: record.subject || '—', wide: true },
              {
                label: translate(t, TRANSLATIONS.ADMIN_MESSAGES_COL_LAST_MESSAGE),
                value: record.lastMessageAt ? `${record.lastMessageText || ''} · ${dayjs(record.lastMessageAt).format('DD/MM HH:mm')}` : '—',
                wide: true,
              },
            ],
            actions: (
              <Button size="small" onClick={() => openDetail(record)}>
                {translate(t, TRANSLATIONS.ADMIN_MESSAGES_OPEN_BUTTON)}
              </Button>
            ),
          })}
          // Lets narrow (mobile) viewports scroll the table horizontally
          // instead of squeezing every column into an unreadable width --
          // 'max-content' sizes each column to its own content rather than a
          // guessed pixel width.
          scroll={{ x: 'max-content' }}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_MESSAGES_COL_USER),
              dataIndex: 'userDisplayName',
              key: 'userDisplayName',
              sorter: textSorter<IAdminConversationSummary>('userDisplayName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_MESSAGES_COL_ROLE),
              dataIndex: 'userRole',
              key: 'userRole',
              filters: enumColumnFilters(['Host', 'Guest'], (value) => translate(t, ROLE_LABEL_KEYS[value])),
              onFilter: (value, record: IAdminConversationSummary) => record.userRole === value,
              render: (value: string) => (ROLE_LABEL_KEYS[value] ? translate(t, ROLE_LABEL_KEYS[value]) : '—'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_MESSAGES_COL_SUBJECT),
              dataIndex: 'subject',
              key: 'subject',
              ellipsis: true,
              render: (value?: string) => value || '—',
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_MESSAGES_COL_LAST_MESSAGE),
              dataIndex: 'lastMessageText',
              key: 'lastMessageText',
              ellipsis: true,
              render: (value: string | undefined, record: IAdminConversationSummary) =>
                record.lastMessageAt ? `${value || ''} · ${dayjs(record.lastMessageAt).format('DD/MM HH:mm')}` : '—',
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_MESSAGES_COL_STATUS),
              dataIndex: 'status',
              key: 'status',
              filters: enumColumnFilters(STATUS_OPTIONS, (value) => translate(t, STATUS_LABEL_KEYS[value])),
              onFilter: (value, record: IAdminConversationSummary) => record.status === value,
              render: (value: string) => <Tag color={STATUS_COLORS[value]}>{translate(t, STATUS_LABEL_KEYS[value])}</Tag>,
            },
            {
              key: 'actions',
              render: (_: unknown, record: IAdminConversationSummary) => (
                <Button size="small" onClick={() => openDetail(record)}>
                  {translate(t, TRANSLATIONS.ADMIN_MESSAGES_OPEN_BUTTON)}
                </Button>
              ),
            },
          ]}
        />
      )}

      {/* width: '100%' on mobile (same responsive pattern as Messages.tsx's
          own booking-details Drawer) -- 480 fixed would either overflow a
          narrow phone screen or get force-clamped by antd, neither of which
          leaves room to actually read a chat. styles.body turns the body
          itself into a flex column filling the Drawer's own full height, so
          the message log below can size itself off that (flex:1/minHeight:0)
          instead of a guessed fixed height that either wastes space on a
          tall screen or gets cramped on a short one. */}
      <Drawer
        title={translate(t, TRANSLATIONS.ADMIN_MESSAGES_THREAD_TITLE)}
        open={detail !== null}
        onClose={() => setDetail(null)}
        width={isMobile ? '100%' : 480}
        styles={{ body: { display: 'flex', flexDirection: 'column', minHeight: 0 } }}
      >
        {detail && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <div style={{ marginBottom: 12, flexShrink: 0 }}>
              <Text strong>{detail.userDisplayName || detail.userId}</Text>
              {detail.userRole && <Tag style={{ marginLeft: 8 }}>{translate(t, ROLE_LABEL_KEYS[detail.userRole])}</Tag>}
              <Tag color={STATUS_COLORS[detail.status]} style={{ marginLeft: 4 }}>
                {translate(t, STATUS_LABEL_KEYS[detail.status])}
              </Tag>
              {detail.subject && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                  {detail.subject}
                </Text>
              )}
            </div>

            {/* flex:1/minHeight:0 -- fills whatever height the flex column
                above actually has, rather than a fixed guess -- with its own
                background+border so it reads as a distinct chat box, not
                blank space next to the input below. */}
            <div
              style={{
                flex: 1,
                minHeight: 160,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                marginBottom: 12,
                padding: 10,
                borderRadius: 12,
                border: `1px solid ${token.colorBorderSecondary}`,
                background: token.colorFillQuaternary,
              }}
            >
              {messagesLoading ? (
                <div style={{ margin: 'auto' }}>
                  <Spin />
                </div>
              ) : messages.length === 0 ? (
                <Text type="secondary" style={{ margin: 'auto', fontSize: 13 }}>
                  {translate(t, TRANSLATIONS.ADMIN_MESSAGES_THREAD_EMPTY)}
                </Text>
              ) : (
                messages.map((m) => (
                  <div key={m.id} style={{ alignSelf: m.isFromAdmin ? 'flex-end' : 'flex-start', maxWidth: '80%', minWidth: 0 }}>
                    <Text type="secondary" style={{ fontSize: 10, display: 'block', marginBottom: 2, textAlign: m.isFromAdmin ? 'right' : 'left' }}>
                      {m.isFromAdmin ? translate(t, TRANSLATIONS.ADMIN_MESSAGES_TEAM_LABEL) : detail.userDisplayName}
                    </Text>
                    <div
                      style={{
                        background: m.isFromAdmin ? CORAL : '#fff',
                        color: m.isFromAdmin ? '#fff' : 'inherit',
                        border: m.isFromAdmin ? 'none' : '1px solid #e8e8e8',
                        borderRadius: 14,
                        padding: '6px 12px',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {m.text}
                    </div>
                  </div>
                ))
              )}
              <div ref={bottomRef} />
            </div>

            <Space style={{ marginBottom: 12, flexShrink: 0 }}>
              <Button size="small" loading={statusChanging} onClick={handleToggleStatus}>
                {translate(t, detail.status === 'Open' ? TRANSLATIONS.ADMIN_MESSAGES_CLOSE_BUTTON : TRANSLATIONS.ADMIN_MESSAGES_REOPEN_BUTTON)}
              </Button>
            </Space>

            {detail.status !== 'Open' && (
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6, flexShrink: 0 }}>
                {translate(t, TRANSLATIONS.ADMIN_MESSAGES_CLOSED_NOTICE)}
              </Text>
            )}
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <TextArea
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={detail.status !== 'Open'}
                autoSize={{ minRows: 1, maxRows: 4 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <Button
                type="primary"
                icon={<IconArrowUp size={16} />}
                loading={sending}
                disabled={detail.status !== 'Open'}
                onClick={handleSend}
                style={{}}
              />
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default MessagesSection;
